import httpClient from '../../../shared/lib/httpClient';
import { ENDPOINTS } from '../../../shared/services/api/endpoints';

/**
 * Checks if an email exists and returns the associated username
 * @param {string} email - Email address to check
 * @returns {Promise} - API response with exists flag and username if found
 */
export const checkEmailExists = async (email) => {
  const response = await httpClient.get(ENDPOINTS.USER.CHECK_EMAIL, { params: { email } });
  return response.data;
};

export const checkUsernameExists = async (username) => {
  const response = await httpClient.get(ENDPOINTS.USER.CHECK_USERNAME, { params: { username } });
  return response.data;
};

/**
 * Adds a new user to the system
 * @param {Object} userData - User data including firstName, lastName, username, email, roles, haloPsaSiteId, haloPsaClientId
 * @returns {Promise} - API response with created user data
 */
export const addUser = async (userData) => {
  const response = await httpClient.post(ENDPOINTS.USER.CREATE, userData);
  return response.data;
};

/**
 * Gets user details for editing
 * @param {number} userId - User ID
 * @returns {Promise} - API response with user details including roles
 */
export const getUserDetails = async (userId) => {
  const response = await httpClient.get(ENDPOINTS.USER.GET_DETAILS(userId));
  return response.data;
};

/**
 * Updates an existing user
 * @param {number} userId - User ID
 * @param {Object} userData - Updated user data including firstName, lastName, username, email, phone, roles
 * @returns {Promise} - API response with updated user data
 */
export const editUser = async (userId, userData) => {
  const response = await httpClient.put(ENDPOINTS.USER.UPDATE(userId), userData);
  return response.data;
};

/**
 * @param {number} userId - Target portal user id (from Edit User modal, not the caller).
 * @returns {Promise<{ success: boolean, message?: string, data?: { removed: boolean } }>}
 */
export const removeUserAuthenticator = async (userId) => {
  const response = await httpClient.delete(ENDPOINTS.USER.REMOVE_AUTHENTICATOR(userId));
  return response.data;
};

/**
 * Resends the invite ("set your password") link to a user still in Invited status, invalidating
 * any previously issued link first. The only call in the editable-email feature that sends mail —
 * editUser() changes the email and invalidates the old link on save, but never sends.
 * @param {number} userId - Target portal user id (from Edit User sheet, not the caller).
 * @returns {Promise} - API response envelope; `data.userId` on success.
 */
export const resendInvite = async (userId) => {
  const response = await httpClient.post(ENDPOINTS.USER.RESEND_INVITE(userId));
  return response.data;
};

/**
 * Deactivates a user in the active firm: releases their licence seats back to the pool, removes the
 * firm from their company picker, and blocks their login if this was their only firm.
 * @param {number} userId - Target portal user id (from Edit User sheet, not the caller).
 * @returns {Promise<{ success: boolean, message?: string, data?: { userId: number, status: string, canStillSignIn: boolean } }>}
 */
export const deactivateUser = async (userId) => {
  const response = await httpClient.post(ENDPOINTS.USER.DEACTIVATE(userId));
  return response.data;
};

/**
 * Reactivates a user in the active firm. Restores access and login only — licences are NOT restored,
 * because the freed seats may already have gone to someone else. Re-assign them explicitly.
 * @param {number} userId - Target portal user id (from Edit User sheet, not the caller).
 * @returns {Promise<{ success: boolean, message?: string, data?: { userId: number, status: string, canStillSignIn: boolean } }>}
 */
export const reactivateUser = async (userId) => {
  const response = await httpClient.post(ENDPOINTS.USER.REACTIVATE(userId));
  return response.data;
};

/**
 * Batch assign/unassign licenses for a product to multiple users
 * @param {string} productName - Product name
 * @param {number} productFirmGroupId - FirmGroupId of the product
 * @param {Array<number>} assignUserIds - Array of user IDs to assign
 * @param {Array<number>} unassignUserIds - Array of user IDs to unassign
 * @returns {Promise} - API response with per-user results
 */
export const batchAssignLicenses = async (productName, productFirmGroupId, assignUserIds, unassignUserIds) => {
  const response = await httpClient.post(ENDPOINTS.USER.BATCH_LICENSES, {
    productName,
    productFirmGroupId,
    assignUserIds,
    unassignUserIds
  });
  return response.data;
};

/**
 * Phase A (Add Seats Now) — bills a mid-cycle seat increase (scoped pro-rata manual invoice +
 * recurring-profile pro-rata + HaloPSA quantity bump) and stages the added-seat users as
 * PendingPayment. Separate from the checkout-page flow. Activation happens after payment (Phase 3).
 * @param {Object} payload - { productName, productFirmGroupId, subscriptionId, productId,
 *   currentSeats, newSeats, assignUserIds, subItems: [{ productId, productName, perSeatPrice,
 *   perSeatCost, taxRate, discountPercent }] }
 */
export const increaseLicenses = async (payload) => {
  const response = await httpClient.post(ENDPOINTS.USER.SEAT_INCREASE, payload);
  return response.data;
};

/**
 * Seat decrease at renewal (US-SP09-ML-003). Persists the per-user removals chosen in the
 * removal overlay without touching the current cycle's paid seats or issuing a refund:
 *   - `removeNowUserIds`     → unassigned immediately; seat empties but stays paid this cycle.
 *   - `expireAtRenewalUserIds` → keep their seat this cycle, flagged to expire on the renewal date.
 * The renewal seat-count drop itself is written separately, by the At Renewal tab through
 * `subscriptionService.setRenewalSeats`.
 * @param {Object} payload - { productName, productFirmGroupId, subscriptionId, productId,
 *   removeNowUserIds: number[], expireAtRenewalUserIds: number[], renewalDate }
 */
export const decreaseLicenses = async (payload) => {
  const response = await httpClient.post(ENDPOINTS.USER.SEAT_DECREASE, payload);
  return response.data;
};


export const updateDataNetEmails = async (userId, dataNetEmails) => {
  const response = await httpClient.patch(ENDPOINTS.USER.UPDATE_DATANET_EMAILS(userId), { dataNetEmails });
  return response.data;
};

export const getDataNetEmails = async (userId) => {
  const response = await httpClient.get(ENDPOINTS.USER.UPDATE_DATANET_EMAILS(userId));
  return response.data;
};
